import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdVerified, MdClose, MdPerson, MdCheckCircle, MdCancel, MdSchedule, MdFilterList } from 'react-icons/md';

function AdminVerifications() {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [actionLoading, setActionLoading] = useState(null);
  const [reviewNotes, setReviewNotes] = useState({});

  const fetchVerifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/verifications?status=${statusFilter}`);
      setVerifications(res.data.verifications || []);
    } catch {
      setVerifications([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchVerifications();
  }, [statusFilter, fetchVerifications]);

  const reviewVerification = async (verificationId, action) => {
    setActionLoading(verificationId);
    try {
      await axios.put(`${API_URL}/api/admin/verifications/${verificationId}`, {
        action,
        notes: reviewNotes[verificationId] || null
      });
      fetchVerifications();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to process verification');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium"><MdVerified className="text-sm" /> Verified</span>;
      case 'submitted':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium"><MdSchedule className="text-sm" /> Awaiting Review</span>;
      case 'rejected':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium"><MdCancel className="text-sm" /> Rejected</span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium"><MdSchedule className="text-sm" /> QR Generated</span>;
      default:
        return <span className="text-xs text-gray-400">{status}</span>;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 font-rubik flex items-center gap-2">
          <MdVerified className="text-blue-500" />
          Identity Verifications
        </h2>
        <div className="flex items-center gap-2">
          <MdFilterList className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            data-testid="verification-status-filter"
          >
            <option value="submitted">Awaiting Review</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="pending">QR Generated</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      ) : verifications.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <MdVerified className="text-5xl mx-auto mb-2 opacity-30" />
          <p className="font-rubik">No {statusFilter === 'all' ? '' : statusFilter} verifications</p>
        </div>
      ) : (
        <div className="space-y-4">
          {verifications.map((v) => (
            <div key={v.verification_id} className="border border-gray-200 rounded-xl p-4" data-testid={`verification-card-${v.verification_id}`}>
              <div className="flex items-start gap-4">
                {/* Profile Photo */}
                <div className="flex-shrink-0">
                  <p className="text-xs text-gray-400 mb-1 text-center">Profile</p>
                  <div className="w-20 h-24 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden border border-gray-300">
                    {v.profile?.profile_picture_url ? (
                      <img src={v.profile.profile_picture_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <MdPerson className="text-3xl text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Selfie Photo */}
                <div className="flex-shrink-0">
                  <p className="text-xs text-gray-400 mb-1 text-center">Selfie</p>
                  <div className="w-20 h-24 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden border border-gray-300">
                    {v.selfie_url ? (
                      <img src={v.selfie_url} alt="Selfie" className="w-full h-full object-cover" />
                    ) : (
                      <MdPerson className="text-3xl text-gray-300" />
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="flex-grow min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-gray-900 font-rubik">{v.profile?.name || 'Unknown'}</h3>
                    {getStatusBadge(v.status)}
                  </div>
                  <p className="text-sm text-gray-500">{v.profile?.office} — {v.profile?.location}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Requested: {new Date(v.created_at).toLocaleDateString()}
                    {v.submitted_at && ` — Selfie submitted: ${new Date(v.submitted_at).toLocaleDateString()}`}
                  </p>
                  {v.admin_notes && (
                    <p className="text-xs text-gray-500 mt-1 italic">Notes: {v.admin_notes}</p>
                  )}

                  {/* Action Buttons (only for submitted) */}
                  {v.status === 'submitted' && (
                    <div className="mt-3 space-y-2">
                      <input
                        type="text"
                        placeholder="Optional admin notes..."
                        value={reviewNotes[v.verification_id] || ''}
                        onChange={(e) => setReviewNotes(prev => ({ ...prev, [v.verification_id]: e.target.value }))}
                        className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200"
                        data-testid={`notes-input-${v.verification_id}`}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => reviewVerification(v.verification_id, 'approve')}
                          disabled={actionLoading === v.verification_id}
                          className="flex-1 px-3 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1"
                          data-testid={`approve-btn-${v.verification_id}`}
                        >
                          <MdCheckCircle className="text-sm" /> Approve
                        </button>
                        <button
                          onClick={() => reviewVerification(v.verification_id, 'reject')}
                          disabled={actionLoading === v.verification_id}
                          className="flex-1 px-3 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
                          data-testid={`reject-btn-${v.verification_id}`}
                        >
                          <MdClose className="text-sm" /> Reject
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminVerifications;
